import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { ContractDetail } from "@ServerTypes/contracts/contracts.schema";

/**
 * State stored under the `contractDetails` redux key. It is the loaded `ContractDetail`
 * server entity returned by `getContracts` (ContractsControllerResponse.result[0]) — both
 * dispatch sites set a full `ContractDetail` — augmented with extra fields the aggregation
 * projects that the base schema does not model. The server-required `ContractDetail` fields
 * (`_id`, `versionId`, `type`, `lockData`, …) are therefore NON-optional here, so consumers
 * read them WITHOUT `!`; the empty initial state is an asserted pre-load placeholder. Only
 * the aggregation extras below are optional (genuinely not always present).
 */
export type ContractDetailsState = ContractDetail & {
  budgetAmount?: number;
  remainingBudgetAmountForMileStones?: number;
  PO_NO?: string;
  allParents?: string[];
  allChilds?: string[];
  isAnyOneChildContractLocked?: boolean;
  companyData?: { name?: string };
  users?: Array<{ id: string; role: string; permissions: number[] }>;
};

const contractSlice = createSlice({
  name: "contractDetails",
  initialState: {} as ContractDetailsState,
  reducers: {
    setContractDetails: (_state: ContractDetailsState, action: PayloadAction<ContractDetailsState | undefined>) =>
      // `{}` when payload is undefined is the cleared/pre-load placeholder (matches initialState).
      ({ ...(action.payload ?? {}) }) as ContractDetailsState,
  },
});

const { setContractDetails: _setContractDetailsRaw } = contractSlice.actions;

/**
 * Object-payload action creator wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setContractDetails = <T extends Exact<ContractDetailsState | undefined, T>>(payload: T) =>
  _setContractDetailsRaw(payload);

export const contractDetails = contractSlice.reducer;

export default contractSlice.reducer;
